import { Bound, Direction, Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import React, { useCallback } from 'react';
import { getNextPosition } from '@/shared/ui/data/EditableGrid/lib/navigation';

interface IOptions {
  bound: Bound;
  onMove?: (from: Position, to: Position) => void;
}

export function useKeyboardNav(options: IOptions) {
  const { bound, onMove } = options;

  const handleArrowKey = useCallback(
    (currentPosition: Position, direction: Direction) => {
      const nextPosition = getNextPosition(currentPosition, bound, {
        type: 'direction',
        direction,
      });
      if (nextPosition !== currentPosition) onMove?.(currentPosition, nextPosition);

      return nextPosition;
    },
    [bound, onMove]
  );

  const handleTab = useCallback(
    (currentPosition: Position, shiftKeyPressed: boolean) => {
      const nextPosition = getNextPosition(currentPosition, bound, {
        type: 'tab',
        shiftKeyPressed,
      });
      if (nextPosition !== currentPosition) onMove?.(currentPosition, nextPosition);

      return nextPosition;
    },
    [bound, onMove]
  );

  const handleEnter = useCallback(
    (currentPosition: Position) => {
      const nextPosition = getNextPosition(currentPosition, bound, {
        type: 'enter',
      });
      if (nextPosition !== currentPosition) onMove?.(currentPosition, nextPosition);

      return nextPosition;
    },
    [bound, onMove]
  );

  const handleNavKeyDown = useCallback(
    (e: React.KeyboardEvent, currentPosition: Position): Position | null => {
      const key = e.key;

      switch (key) {
        case 'ArrowUp':
          e.preventDefault();
          return handleArrowKey(currentPosition, 'up');
        case 'ArrowDown':
          e.preventDefault();
          return handleArrowKey(currentPosition, 'down');
        case 'ArrowLeft':
          e.preventDefault();
          return handleArrowKey(currentPosition, 'left');
        case 'ArrowRight':
          e.preventDefault();
          return handleArrowKey(currentPosition, 'right');
        case 'Tab':
          e.preventDefault();
          return handleTab(currentPosition, e.shiftKey);
        default:
          return null;
      }
    },
    [handleArrowKey, handleTab]
  );

  return { handleNavKeyDown };
}
